import { useEffect, useMemo, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SectionTitle, StatCard } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

const empty = () => ({
  entry_date: "", entries_open: "", entries_close: "", show_name: "", location: "", projected_cost: "", notes: "",
});

function currency(v) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export default function ShowPlansPage() {
  const { activeDog } = useDogs();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // entry being edited (or null for new)
  const [form, setForm] = useState(empty());

  const load = async () => {
    if (!activeDog) return;
    const { data } = await api.get("/show-plan-entries", { params: { dog_id: activeDog.id } });
    setItems(data.items || []);
    setTotal(data.total_projected_cost || 0);
  };

  useEffect(() => { load(); }, [activeDog]);

  const upcoming = useMemo(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    return items.filter((x) => !x.entry_date || x.entry_date >= today).length;
  }, [items]);

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      entry_date: it.entry_date || "",
      entries_open: it.entries_open || "",
      entries_close: it.entries_close || "",
      show_name: it.show_name || "",
      location: it.location || "",
      projected_cost: it.projected_cost == null ? "" : String(it.projected_cost),
      notes: it.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.show_name) return;
    const body = {
      dog_id: activeDog.id,
      entry_date: form.entry_date || null,
      entries_open: form.entries_open || null,
      entries_close: form.entries_close || null,
      show_name: form.show_name,
      location: form.location,
      projected_cost: parseFloat(form.projected_cost) || 0,
      notes: form.notes,
    };
    try {
      if (editing) {
        await api.put(`/show-plan-entries/${editing.id}`, body);
        toast.success("Updated");
      } else {
        await api.post("/show-plan-entries", body);
        toast.success("Added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/show-plan-entries/${id}`);
    load();
  };

  return (
    <div className="space-y-8" data-testid="show-plans-page">
      <SectionTitle
        overline="Budget & planning"
        title="Show / Entry Plans"
        action={
          <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-show-plan-button">
            <Plus size={16} className="mr-1.5" /> Add show
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <StatCard testid="stat-show-count" label="Planned shows" value={items.length} hint={`${upcoming} upcoming`} />
        <StatCard testid="stat-projected-cost" label="Projected total" value={currency(total)} hint="all planned entries" />
        <StatCard testid="stat-avg-cost" label="Avg per show" value={items.length ? currency(total / items.length) : currency(0)} hint="entry fees, travel, etc." />
      </div>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground p-10 text-center">No shows planned yet — click <strong>Add show</strong> to start your budget.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Show</th>
                  <th className="px-6 py-3">Location</th>
                  <th className="px-6 py-3 text-right">Projected cost</th>
                  <th className="px-6 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-border" data-testid={`show-plan-row-${it.id}`}>
                    <td className="px-6 py-3 whitespace-nowrap">
                      {it.entry_date ? format(parseISO(it.entry_date), "MMM d, yyyy") : <span className="text-muted-foreground">TBD</span>}
                    </td>
                    <td className="px-6 py-3">
                      <p className="font-medium">{it.show_name}</p>
                      {(it.entries_open || it.entries_close) ? (
                        <p className="text-xs text-muted-foreground mt-0.5" data-testid={`entries-window-${it.id}`}>
                          Entries {it.entries_open ? format(parseISO(it.entries_open), "MMM d") : "TBD"} – {it.entries_close ? format(parseISO(it.entries_close), "MMM d") : "TBD"}
                        </p>
                      ) : null}
                      {it.notes ? <p className="text-xs text-muted-foreground mt-0.5">{it.notes}</p> : null}
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">{it.location || "—"}</td>
                    <td className="px-6 py-3 text-right tabular-nums" data-testid={`projected-cost-${it.id}`}>
                      {it.projected_cost ? currency(it.projected_cost) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(it)} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground" data-testid={`edit-show-plan-${it.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => remove(it.id)} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-secondary">
                  <td className="px-6 py-3 font-medium" colSpan={3}>Total</td>
                  <td className="px-6 py-3 text-right font-display font-bold tabular-nums" data-testid="total-projected-cost">{currency(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="show-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">
              {editing ? "Edit show plan" : "Add show plan"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Show name</Label>
              <Input value={form.show_name} onChange={(e) => setForm({ ...form, show_name: e.target.value })} required className="mt-1.5" data-testid="show-name-input" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Entries open</Label>
                <Input type="date" value={form.entries_open} onChange={(e) => setForm({ ...form, entries_open: e.target.value })} className="mt-1.5" data-testid="entries-open-input" />
              </div>
              <div>
                <Label>Entries close</Label>
                <Input type="date" value={form.entries_close} onChange={(e) => setForm({ ...form, entries_close: e.target.value })} className="mt-1.5" data-testid="entries-close-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Show date</Label>
                <Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="mt-1.5" data-testid="show-date-input" />
              </div>
              <div>
                <Label>Projected cost</Label>
                <Input type="number" step="0.01" min="0" value={form.projected_cost} onChange={(e) => setForm({ ...form, projected_cost: e.target.value })} className="mt-1.5" data-testid="projected-cost-input" placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" placeholder="City, venue, etc." />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Entry deadline, handler, hotel, things to pack…" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-show-plan-button">
                {editing ? "Save changes" : "Add show"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
